import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, RefreshCw, ChevronLeft, User, ShoppingCart, CreditCard, Truck, Package, Music } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const OrderDetail = ({ orderId, onBack, hideFinancials }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerMembership, setCustomerMembership] = useState(null);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        const r = await axios.get(`${API}/api/admin/orders/${orderId}/detail`, { withCredentials: true });
        setDetail(r.data);
        // Load customer's other orders if we have email
        if (r.data.customer_email && r.data.customer_email !== 'N/A') {
          try {
            const allOrders = await axios.get(`${API}/api/admin/orders`, { withCredentials: true });
            const customerOrd = (allOrders.data || []).filter(o =>
              (o.user_email || o.email) === r.data.customer_email && o.order_id !== orderId
            );
            setCustomerOrders(customerOrd.slice(0, 10));
          } catch { }
          // Check membership
          try {
            const memberships = await axios.get(`${API}/api/admin/memberships`, { withCredentials: true });
            const match = (memberships.data || []).find(m =>
              (m.user_email || m.email) === r.data.customer_email && m.status === 'active'
            );
            setCustomerMembership(match || null);
          } catch { }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    loadDetail();
  }, [orderId]);

  if (loading) return <p className="text-white/40">Cargando detalle...</p>;
  if (!detail) return <p className="text-white/40">Orden no encontrada</p>;

  const handleSendDelivery = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/orders/${orderId}/deliver`, {}, { withCredentials: true });
      alert(res.data.status === 'sent' ? 'Email de entrega enviado!' : `Fallo: ${res.data.error || 'Error'}`);
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div data-testid="order-detail-view">
      <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 text-sm" data-testid="order-detail-back">
        <ChevronLeft className="w-4 h-4" />Volver a Ordenes
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Detalle de Orden</h2>
          <p className="text-white/40 text-xs font-mono mt-1">{detail.order_id}</p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
          detail.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
        }`}>{detail.payment_status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-[#E8720C]" />
            <h3 className="font-semibold text-sm">Cliente</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Nombre</span>
              <span className="font-medium">{detail.customer_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Email</span>
              <span className="text-white/70 font-mono text-xs">{detail.customer_email}</span>
            </div>
            {customerMembership && (
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Plan Activo</span>
                <span className="text-[#E8720C] font-medium">{customerMembership.name || customerMembership.plan_name || 'Miembro'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Music className="w-4 h-4 text-[#E8720C]" />
            <h3 className="font-semibold text-sm">Producto</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Producto</span>
              <span className="font-medium">{detail.item_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Tipo</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-white/10">{detail.item_type}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Licencia</span>
              <span className="text-white/70">{(detail.license_type || 'basic').toUpperCase()}</span>
            </div>
            {detail.track_info && (
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Artista</span>
                <span className="text-white/70">{detail.track_info.artist || '-'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Info */}
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-[#E8720C]" />
            <h3 className="font-semibold text-sm">Pago</h3>
          </div>
          <div className="space-y-2">
            {!hideFinancials && (
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Monto</span>
                <span className="gold-text font-bold text-lg">${detail.amount}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Metodo</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${detail.payment_method === 'crypto' ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-[#0070BA]/20 text-[#0070BA]'}`}>
                {(detail.payment_method || 'paypal').toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Fecha</span>
              <span className="text-white/60">{detail.created_at ? new Date(detail.created_at).toLocaleString() : '-'}</span>
            </div>
            {detail.paid_at && (
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Pagado</span>
                <span className="text-green-400">{new Date(detail.paid_at).toLocaleString()}</span>
              </div>
            )}
          </div>
          {detail.crypto && (
            <div className="mt-3 p-3 bg-white/[0.03] rounded-lg border border-white/5">
              {/* CRYPTO ALERT */}
              {detail.crypto.status !== 'approved' && (
                <div className="mb-3 p-3 bg-red-500/10 border-2 border-red-500/40 rounded-xl" data-testid="crypto-verify-alert-detail">
                  <p className="text-red-400 font-bold text-sm text-center uppercase tracking-wide">
                    VERIFICA TU BILLETERA PRIMERO ANTES DE APROBAR ESTA SOLICITUD
                  </p>
                </div>
              )}
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Info Crypto</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-white/40">Red</span><span>{detail.crypto.method}</span></div>
                <div className="flex justify-between"><span className="text-white/40">Estado</span><span>{detail.crypto.status}</span></div>
                {detail.crypto.proof_value && <div className="flex justify-between"><span className="text-white/40">Prueba</span><span className="truncate max-w-[200px]">{detail.crypto.proof_value}</span></div>}
              </div>
            </div>
          )}
        </div>

        {/* Delivery Info */}
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-4 h-4 text-[#E8720C]" />
            <h3 className="font-semibold text-sm">Entrega</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Estado</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                detail.delivery?.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                detail.delivery?.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {detail.delivery?.status === 'sent' ? 'Enviado' : detail.delivery?.status === 'failed' ? 'Fallido' : 'No enviado'}
              </span>
            </div>
            {detail.delivery?.sent_at && (
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Enviado</span>
                <span className="text-white/60">{new Date(detail.delivery.sent_at).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-white/40">MP3</span>
              <span className={detail.delivery?.mp3_available ? 'text-green-400' : 'text-red-400'}>{detail.delivery?.mp3_available ? 'Disponible' : 'No disponible'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">WAV</span>
              <span className={detail.delivery?.wav_available ? 'text-green-400' : 'text-red-400'}>{detail.delivery?.wav_available ? 'Disponible' : 'No disponible'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/40">Licencia PDF</span>
              <span className={detail.delivery?.license_generated ? 'text-green-400' : 'text-white/40'}>{detail.delivery?.license_generated ? 'Generada' : 'No generada'}</span>
            </div>
          </div>
          {detail.payment_status === 'paid' && (
            <button onClick={handleSendDelivery}
              className="mt-4 w-full py-2 bg-[#E8720C]/10 hover:bg-[#E8720C]/20 text-[#E8720C] rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-colors" data-testid="detail-send-delivery">
              <Mail className="w-3.5 h-3.5" />{detail.delivery?.delivered_to_portal ? 'Reenviar Entrega' : 'Enviar Entrega'}
            </button>
          )}
        </div>
      </div>

      {/* Customer's Other Orders */}
      {customerOrders.length > 0 && (
        <div className="glass-card p-5 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="w-4 h-4 text-[#E8720C]" />
            <h3 className="font-semibold text-sm">Otras Compras del Cliente ({customerOrders.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/40 border-b border-white/10 text-xs">
                <th className="p-3">Producto</th>
                <th className="p-3">Tipo</th>
                {!hideFinancials && <th className="p-3">Monto</th>}
                <th className="p-3">Estado</th>
                <th className="p-3">Fecha</th>
              </tr></thead>
              <tbody>
                {customerOrders.map((o, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-3 font-medium">{o.item_name}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full text-xs bg-white/10">{o.item_type || 'track'}</span></td>
                    {!hideFinancials && <td className="p-3 gold-text">${o.amount}</td>}
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{o.payment_status}</span></td>
                    <td className="p-3 text-white/40 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const OrdersPanel = ({ hideFinancials = false }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/orders`, { withCredentials: true });
      setOrders(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSendDelivery = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/admin/orders/${orderId}/deliver`, {}, { withCredentials: true });
      alert(res.data.status === 'sent' ? 'Email de entrega enviado!' : `Fallo: ${res.data.error || 'Error'}`);
      load();
    } catch (err) { alert('Error enviando entrega: ' + (err.response?.data?.detail || err.message)); }
  };

  if (loading) return <p className="text-white/40">Cargando ordenes...</p>;

  if (selectedOrder) {
    return <OrderDetail orderId={selectedOrder} onBack={() => setSelectedOrder(null)} hideFinancials={hideFinancials} />;
  }

  return (
    <div data-testid="orders-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Ordenes ({orders.length})</h2>
        <button onClick={load} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">ID</th>
              <th className="p-4">Producto</th>
              {!hideFinancials && <th className="p-4">Monto</th>}
              <th className="p-4">Metodo</th>
              <th className="p-4">Estado</th>
              <th className="p-4">Entrega</th>
              <th className="p-4">Cliente</th>
              <th className="p-4">Fecha</th>
              <th className="p-4">Acciones</th>
            </tr></thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => setSelectedOrder(order.order_id)} data-testid={`order-row-${order.id}`}>
                  <td className="p-4 font-mono text-xs text-white/50">{(order.order_id || '').slice(0, 12)}...</td>
                  <td className="p-4 font-medium">{order.item_name}</td>
                  {!hideFinancials && <td className="p-4 gold-text font-medium">${order.amount}</td>}
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${order.payment_method === 'crypto' ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-[#0070BA]/20 text-[#0070BA]'}`}>
                      {(order.payment_method || 'paypal').toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.delivery_status === 'sent' ? 'bg-green-500/20 text-green-400' :
                      order.delivery_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-white/10 text-white/40'
                    }`}>
                      {order.delivery_status === 'sent' ? 'Enviado' : order.delivery_status === 'failed' ? 'Fallido' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="p-4 text-white/60">{order.user_email || 'Guest'}</td>
                  <td className="p-4 text-white/40">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</td>
                  <td className="p-4" onClick={e => e.stopPropagation()}>
                    {order.payment_status === 'paid' && (
                      <button onClick={() => handleSendDelivery(order.order_id)}
                        className="px-3 py-1.5 bg-[#E8720C]/10 hover:bg-[#E8720C]/20 text-[#E8720C] rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                        data-testid={`send-delivery-${order.id}`}>
                        <Mail className="w-3 h-3" />{order.delivery_status === 'sent' ? 'Reenviar' : 'Enviar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && <p className="text-center text-white/40 py-8">Sin ordenes</p>}
      </div>
    </div>
  );
};

export default OrdersPanel;
